import {useEffect, useState, useCallback} from 'react';

import {loadScript} from 'coreUtils/utils';
import type TPaymentResult from 'coreClasses/payments/types/TPaymentResult';
import type {TApplePaySafeChargeConfig} from 'coreClasses/applepay/types/TApplePayAPI';
import ApplePayProcessor from 'coreClasses/applepay/ApplePayProcessor';
import {isApplePaySupported} from 'coreUtils/utils';
import {useLocalesMgr, useRequestClass} from 'coreHooks';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';

let initializationPromise;
let globalApplePayProcessor;

/**
 * useApplePaySafeCharge custom hook.
 * @param {boolean} [isActive=false] - indicate whatever this hook is active
 * @param {TApplePaySafeChargeConfig} config - ApplePay configuration
 */
const useApplePaySafeCharge = (isActive: boolean, config: TApplePaySafeChargeConfig) => {
	const {managerRepository} = useGeneralProvider();
	const [loaded, setLoaded] = useState(!!globalApplePayProcessor);
	const [loading, setLoading] = useState(!!initializationPromise);
	const sessionCurrency = useLocalesMgr().getCurrentLocale().getCurrencyCode();
	const {isProduction} = useRequestClass();

	useEffect(() => {
		if (
			isActive &&
			isApplePaySupported() &&
			(!globalApplePayProcessor || globalApplePayProcessor.config.currencyCode !== sessionCurrency) &&
			!initializationPromise
		) {
			setLoading(true);

			initializationPromise = new Promise<void>((resolve) => {
				loadScript(
					'https://cdn.safecharge.com/safecharge_resources/v1/sc_api_applepay.min.js',
					'safecharge_applepay'
				).then(() => {
					globalApplePayProcessor = new ApplePayProcessor(
						managerRepository,
						config,
						!isProduction
					);
					globalApplePayProcessor.initialize(() => {
						setLoaded(true);
						setLoading(false);
						resolve();
						initializationPromise = null;
					});
				}).catch(() => {
					setLoading(false);
				});
			});
		} else if (initializationPromise) {
			initializationPromise.then(() => {
				setLoaded(true);
				setLoading(false);
			});
		}
	}, [isActive]);

	const handlePayment = useCallback((productID?: string): Promise<TPaymentResult> => {
		return new Promise((resolve) => {
			globalApplePayProcessor.handlePaymentAuthorized = (redirectURL: string) => {
				resolve({
					success: true,
					status: 'completed',
					redirectURL: redirectURL
				});
			};

			globalApplePayProcessor.handleCancel = () => {
				resolve({
					success: false,
					status: 'canceled',
				});
			};

			globalApplePayProcessor.handleError = (error: Error) => {
				resolve({
					success: false,
					status: 'error',
					error: error
				});
			};

			globalApplePayProcessor.applePayHandler(productID);
		});
	}, []);

	return {
		loaded,
		loading,
		handle: handlePayment
	};
};

export default useApplePaySafeCharge;
